// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the words for a machine's health and activity (docs/konvo-agents/HEALTH.md): each check's
// name and short state, the line a problem gets when the Health section is closed, every activity
// kind's title and sub line, what Konvo did about a problem, day headings, and the plain-text block
// "Copy for support" puts on the clipboard. The API sends codes; the app words them all here.

import {clockTime, formatDuration, itemTime, offlineSince} from '@app/features/konvo_agents/components/AgentsCopy';
import {accountLabel} from '@app/features/konvo_agents/state/AgentAccounts';
import {problemChecks} from '@app/features/konvo_agents/state/AgentHealth';
import type {
	AgentActivityEvent,
	AgentHealthCheck,
	AgentMachineHealth,
	AgentMachineView,
} from '@konvo/schema/src/domains/konvo/Agents';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

/** "Claude Code" / "Codex" for a driver or provider id; the id itself for anything else. */
export function agentName(driver: string | undefined): string {
	if (!driver) return '';
	if (/codex/i.test(driver)) return 'Codex';
	if (/claude/i.test(driver)) return 'Claude Code';
	return driver;
}

/** "Claude" / "Codex": the brand an account name goes with. */
function brand(driver: string | undefined): string {
	if (/codex/i.test(driver ?? '')) return 'Codex';
	if (/claude/i.test(driver ?? '')) return 'Claude';
	return '';
}

/** "Claude · Nuvio", "Claude · Personal", "Codex": an account as pickers name it. */
export function accountName(account: string | undefined, driver: string | undefined): string {
	const label = accountLabel(account ?? '', '');
	const brandName = brand(driver);
	if (!label) return brandName || agentName(driver);
	if (!brandName || label.toLowerCase() === brandName.toLowerCase()) return label;
	return `${brandName} · ${label}`;
}

const KONVO = msg({message: 'Konvo', comment: 'Agents health: the check that the machine reaches Konvo.'});

/** A check's name: Konvo, konvo-agents, the tool ("T3 Code") or the account. */
export function checkName(i18n: I18n, check: AgentHealthCheck, toolName: string): string {
	switch (check.kind) {
		case 'konvo':
			return i18n._(KONVO);
		case 'bridge':
			return 'konvo-agents';
		case 'tool':
			return toolName;
		default:
			return accountName(check.account, check.driver ?? check.provider);
	}
}

const CONNECTED = msg({message: 'Connected', comment: 'Agents health: the machine reaches Konvo.'});
const VERSION_LATEST = msg({
	message: '{version} · latest',
	comment: 'Agents health: konvo-agents runs the newest version, e.g. "0.50.0 · latest".',
});
const VERSION_OUT = msg({
	message: '{version} · {latest} is out',
	comment: 'Agents health: a newer version is out, e.g. "0.159.1 · 0.163.0 is out".',
});
const CANT_UPDATE_ITSELF = msg({
	message: "{version} · can't update itself",
	comment: 'Agents health: konvo-agents failed to update itself; {version} is the one it runs.',
});
const ANSWERING = msg({
	message: '{version} · answering',
	comment: 'Agents health: the agent tool (T3 Code) answers; {version} is its version, e.g. "0.1.3".',
});
const NOT_ANSWERING = msg({
	message: 'Not answering',
	comment: 'Agents health: the agent tool (T3 Code) does not answer.',
});
const SIGNED_IN = msg({message: 'Signed in', comment: 'Agents health: a Claude or Codex account is signed in.'});
const SIGNED_OUT = msg({message: 'Signed out', comment: 'Agents health: a Claude or Codex account needs signing in.'});
const SIGNED_OUT_AT = msg({
	message: 'Signed out {time}',
	comment: 'Agents health: when an account signed out, e.g. "Signed out 06:39" or "Signed out Yesterday 06:39".',
});
const NOT_INSTALLED = msg({
	message: 'Not installed',
	comment: 'Agents health: Claude Code or Codex is not installed on the machine.',
});
const UNAVAILABLE = msg({
	message: 'Unavailable',
	comment: 'Agents health: the account is configured but T3 Code cannot use it right now.',
});
const TURNED_OFF = msg({message: 'Turned off', comment: 'Agents health: the account is turned off.'});
const STATE_OK = msg({message: 'OK', comment: 'Agents health: a check is fine (fallback).'});
const STATE_WARN = msg({message: 'Needs a look', comment: 'Agents health: a check needs a look (fallback).'});
const STATE_BAD = msg({message: 'Not working', comment: 'Agents health: a check is failing (fallback).'});
const STATE_OFF = msg({message: 'Off', comment: 'Agents health: a check is off (fallback).'});

/** A check's short state beside its name: "0.1.3 · answering", "Signed out 06:39", "Offline since 13:10". */
export function checkState(i18n: I18n, check: AgentHealthCheck, now: number): string {
	const version = check.version ?? '';
	const latest = check.latestVersion ?? '';
	switch (check.reason) {
		case 'offline':
			return offlineSince(i18n, check.since ?? null, now);
		case 'behind':
		case 'updateAvailable':
			return latest ? i18n._(VERSION_OUT, {version, latest}) : version;
		case 'updateFailed':
			return i18n._(CANT_UPDATE_ITSELF, {version});
		case 'toolDown':
			return i18n._(NOT_ANSWERING);
		case 'signIn': {
			const at = check.since ? Date.parse(check.since) : Number.NaN;
			return Number.isFinite(at) ? i18n._(SIGNED_OUT_AT, {time: itemTime(i18n, at, now)}) : i18n._(SIGNED_OUT);
		}
		case 'notInstalled':
			return i18n._(NOT_INSTALLED);
		case 'unavailable':
			return i18n._(UNAVAILABLE);
		case 'disabled':
			return i18n._(TURNED_OFF);
	}
	if (check.state === 'ok') {
		if (check.kind === 'konvo') return i18n._(CONNECTED);
		if (check.kind === 'bridge') return version ? i18n._(VERSION_LATEST, {version}) : i18n._(STATE_OK);
		if (check.kind === 'tool') return version ? i18n._(ANSWERING, {version}) : i18n._(STATE_OK);
		if (check.kind === 'account') return i18n._(SIGNED_IN);
		return i18n._(STATE_OK);
	}
	if (check.state === 'warn') return i18n._(STATE_WARN);
	if (check.state === 'bad') return i18n._(STATE_BAD);
	return i18n._(STATE_OFF);
}

const PROBLEM_BEHIND = msg({
	message: 'konvo-agents {latest} is out',
	comment: 'Agents health, one line: a newer konvo-agents is out for the machine.',
});
const PROBLEM_UPDATE_FAILED = msg({
	message: "konvo-agents can't update itself",
	comment: 'Agents health, one line: konvo-agents failed to update itself on the machine.',
});
const PROBLEM_TOOL_DOWN = msg({
	message: "{tool} isn't answering",
	comment: 'Agents health, one line: the agent tool (e.g. "T3 Code") does not answer.',
});
const PROBLEM_SIGN_IN = msg({
	message: '{name} signed out',
	comment: 'Agents health, one line: an account needs signing in again, e.g. "Claude · Nuvio signed out".',
});
const PROBLEM_NOT_INSTALLED = msg({
	message: "{name} isn't installed",
	comment: 'Agents health, one line: Claude Code or Codex is not installed, e.g. "Codex isn\'t installed".',
});
const PROBLEM_UNAVAILABLE = msg({
	message: "{name} can't be used right now",
	comment: 'Agents health, one line: an account is configured but unavailable.',
});
const PROBLEM_UPDATE = msg({
	message: '{name} update ready',
	comment: 'Agents health, one line: a newer Claude Code or Codex is out, e.g. "Codex update ready".',
});
const PROBLEM_OTHER = msg({
	message: '{name} needs a look',
	comment: 'Agents health, one line: a check has a problem Konvo has no words for.',
});

/** A problem in a few words, for the closed Health line and the Agents list: "Claude · Nuvio signed out". */
export function problemText(i18n: I18n, check: AgentHealthCheck, toolName: string, now: number): string {
	const name = checkName(i18n, check, toolName);
	switch (check.reason) {
		case 'offline':
			return offlineSince(i18n, check.since ?? null, now);
		case 'behind':
			return i18n._(PROBLEM_BEHIND, {latest: check.latestVersion ?? ''});
		case 'updateFailed':
			return i18n._(PROBLEM_UPDATE_FAILED);
		case 'toolDown':
			return i18n._(PROBLEM_TOOL_DOWN, {tool: toolName});
		case 'signIn':
			return i18n._(PROBLEM_SIGN_IN, {name});
		case 'notInstalled':
			return i18n._(PROBLEM_NOT_INSTALLED, {name: agentName(check.driver ?? check.provider) || name});
		case 'unavailable':
			return i18n._(PROBLEM_UNAVAILABLE, {name});
		case 'updateAvailable':
			return i18n._(PROBLEM_UPDATE, {name: agentName(check.driver ?? check.provider) || name});
		default:
			return i18n._(PROBLEM_OTHER, {name});
	}
}

const SIGN_IN_WHY = msg({
	message: 'Chats on {name} stop until you sign in.',
	comment: 'Agents list: under "Claude · Nuvio signed out"; {name} is the account.',
});
const UPDATE_FAILED_WHY = msg({
	message: 'Run the update on the machine once.',
	comment: 'Agents list: under "konvo-agents can\'t update itself".',
});
const TOOL_DOWN_WHY = msg({
	message: 'Prompts wait until it answers again.',
	comment: 'Agents list: under "T3 Code isn\'t answering".',
});

/** One more line for the problem under the machine picker, when there is something useful to add. */
export function problemWhy(i18n: I18n, check: AgentHealthCheck, toolName: string): string | null {
	if (check.reason === 'signIn') return i18n._(SIGN_IN_WHY, {name: checkName(i18n, check, toolName)});
	if (check.reason === 'updateFailed') return check.detail || i18n._(UPDATE_FAILED_WHY);
	if (check.reason === 'toolDown') return check.detail || i18n._(TOOL_DOWN_WHY);
	return null;
}

const ALL_GOOD = msg({
	message: 'All good · checked {time}',
	comment: 'Agents health, closed: nothing needs a look; {time} is when it was checked, e.g. "15:08".',
});
const PROBLEMS_WEEK = msg({
	message: '{count, plural, one {# problem this week} other {# problems this week}}',
	comment: 'Agents health, closed: warnings and errors in the last 7 days.',
});

export interface HealthSummary {
	/** The lead line: the main problem, "Offline since …" or "All good · checked 15:08". */
	lead: string;
	/** Other problems and the week's count, joined with " · " after the lead. */
	rest: Array<string>;
	main: AgentHealthCheck | null;
}

/** What the closed Health line says. */
export function healthSummary(
	i18n: I18n,
	health: AgentMachineHealth,
	machine: Pick<AgentMachineView, 'online' | 'lastSeenAt'>,
	toolName: string,
	now: number,
): HealthSummary {
	const week = health.problemsThisWeek > 0 ? [i18n._(PROBLEMS_WEEK, {count: health.problemsThisWeek})] : [];
	if (!machine.online) return {lead: offlineSince(i18n, machine.lastSeenAt, now), rest: week, main: null};
	const problems = problemChecks(health);
	if (!problems.length)
		return {lead: i18n._(ALL_GOOD, {time: clockTime(i18n, Date.parse(health.checkedAt))}), rest: week, main: null};
	const [main, ...others] = problems;
	return {
		lead: problemText(i18n, main, toolName, now),
		rest: [...others.slice(0, 2).map((check) => problemText(i18n, check, toolName, now)), ...week],
		main,
	};
}

// ── Activity ────────────────────────────────────────────────────────────

const EV_CONNECTED = msg({message: 'Back online', comment: 'Agents activity: the machine reached Konvo again.'});
const EV_CONNECTED_AFTER = msg({
	message: 'Back online after {duration}',
	comment: 'Agents activity: the machine reached Konvo again; {duration} is how long it was away, e.g. "2m 10s".',
});
const EV_DISCONNECTED = msg({
	message: 'Lost Konvo',
	comment: 'Agents activity: the machine stopped reaching Konvo.',
});
const EV_BRIDGE_UPDATED = msg({
	message: 'konvo-agents updated to {version}',
	comment: 'Agents activity: konvo-agents on the machine now runs {version}.',
});
const EV_BRIDGE_FAILED = msg({
	message: 'Update to {version} failed',
	comment: 'Agents activity: konvo-agents could not update itself to {version}.',
});
const EV_BRIDGE_FAILED_PLAIN = msg({
	message: "konvo-agents couldn't update itself",
	comment: 'Agents activity: konvo-agents could not update itself (version unknown).',
});
const EV_TOOL_DOWN = msg({
	message: '{tool} stopped answering',
	comment: 'Agents activity: the agent tool (e.g. "T3 Code") stopped answering.',
});
const EV_TOOL_UP = msg({
	message: '{tool} answers again',
	comment: 'Agents activity: the agent tool (e.g. "T3 Code") answers again.',
});
const EV_TOOL_DOWN_FOR = msg({
	message: "{tool} didn't answer for {duration}",
	comment: 'Agents activity: the agent tool (e.g. "T3 Code") did not answer; {duration} is how long, e.g. "40s".',
});
const EV_TOOL_UP_AFTER = msg({
	message: '{tool} answers again after {duration}',
	comment: 'Agents activity: the agent tool answers again; {duration} is how long it was down, e.g. "40s".',
});
const EV_TOOL_UPDATED = msg({
	message: '{tool} updated to {version}',
	comment: 'Agents activity: the agent tool (e.g. "T3 Code") now runs {version}.',
});
const EV_SIGNED_OUT = msg({
	message: '{name} signed out',
	comment: 'Agents activity: an account needs signing in again, e.g. "Claude · Nuvio signed out".',
});
const EV_SIGNED_IN = msg({
	message: '{name} signed in',
	comment: 'Agents activity: an account is signed in and ready again.',
});
const EV_AGENT_UPDATED = msg({
	message: '{agent} updated to {version}',
	comment: 'Agents activity: Claude Code or Codex updated, e.g. "Codex updated to 0.163.0".',
});
const EV_AGENT_FAILED = msg({
	message: "{agent} couldn't update",
	comment: 'Agents activity: updating Claude Code or Codex failed.',
});
const EV_SEND_FAILED = msg({
	message: "A prompt didn't go through",
	comment: 'Agents activity: a prompt sent from Konvo to a chat failed.',
});
const EV_START_FAILED = msg({
	message: "A new chat didn't start",
	comment: 'Agents activity: starting a chat from Konvo failed.',
});
const EV_ANSWER_FAILED = msg({
	message: "An answer didn't go through",
	comment: "Agents activity: answering an agent's question or approval from Konvo failed.",
});
const EV_ACCOUNT_FAILED = msg({
	message: "An account change didn't go through",
	comment: 'Agents activity: adding, changing or removing an account from Konvo failed.',
});
const EV_ACTION_FAILED = msg({
	message: "Something asked from Konvo didn't work",
	comment: 'Agents activity: another request from Konvo to the machine failed.',
});
const EV_ACCOUNT_ADDED = msg({
	message: '{name} added',
	comment: 'Agents activity: an account was added from Konvo, e.g. "Claude · Work 2 added".',
});
const EV_ACCOUNT_CHANGED = msg({
	message: '{name} changed',
	comment: 'Agents activity: an account was edited from Konvo.',
});
const EV_ACCOUNT_REMOVED = msg({
	message: '{name} removed',
	comment: 'Agents activity: an account was removed from Konvo.',
});
const EV_TIMES = msg({
	message: '{count, plural, one {# time} other {# times}}',
	comment: 'Agents activity: the same thing happened this many times within minutes, after its title.',
});

function eventAccount(event: AgentActivityEvent): string {
	return accountName(event.account, event.provider);
}

/** An event's title, worded per kind: "Claude · Nuvio signed out", "A prompt didn't go through". */
export function eventTitle(i18n: I18n, event: AgentActivityEvent, toolName: string): string {
	const version = event.version ?? '';
	const duration = event.seconds != null && event.seconds > 0 ? formatDuration(i18n, event.seconds) : null;
	switch (event.kind) {
		case 'connected':
			return duration ? i18n._(EV_CONNECTED_AFTER, {duration}) : i18n._(EV_CONNECTED);
		case 'disconnected':
			return i18n._(EV_DISCONNECTED);
		case 'bridgeUpdated':
			return i18n._(EV_BRIDGE_UPDATED, {version});
		case 'bridgeUpdateFailed':
			return version ? i18n._(EV_BRIDGE_FAILED, {version}) : i18n._(EV_BRIDGE_FAILED_PLAIN);
		case 'toolDown':
			return duration ? i18n._(EV_TOOL_DOWN_FOR, {tool: toolName, duration}) : i18n._(EV_TOOL_DOWN, {tool: toolName});
		case 'toolUp':
			return duration ? i18n._(EV_TOOL_UP_AFTER, {tool: toolName, duration}) : i18n._(EV_TOOL_UP, {tool: toolName});
		case 'toolUpdated':
			return i18n._(EV_TOOL_UPDATED, {tool: toolName, version});
		case 'signedOut':
			return i18n._(EV_SIGNED_OUT, {name: eventAccount(event)});
		case 'signedIn':
			return i18n._(EV_SIGNED_IN, {name: eventAccount(event)});
		case 'agentUpdated':
			return i18n._(EV_AGENT_UPDATED, {agent: agentName(event.provider), version});
		case 'agentUpdateFailed':
			return i18n._(EV_AGENT_FAILED, {agent: agentName(event.provider)});
		case 'actionFailed':
			switch (event.op) {
				case 'send':
					return i18n._(EV_SEND_FAILED);
				case 'start':
					return i18n._(EV_START_FAILED);
				case 'respond':
				case 'answer':
				case 'dismiss':
					return i18n._(EV_ANSWER_FAILED);
				case 'account':
					return i18n._(EV_ACCOUNT_FAILED);
				default:
					return i18n._(EV_ACTION_FAILED);
			}
		case 'accountAdded':
			return i18n._(EV_ACCOUNT_ADDED, {name: eventAccount(event)});
		case 'accountChanged':
			return i18n._(EV_ACCOUNT_CHANGED, {name: eventAccount(event)});
		case 'accountRemoved':
			return i18n._(EV_ACCOUNT_REMOVED, {name: eventAccount(event)});
	}
}

/** "· 49 times" after a title, when the same thing came again within minutes. */
export function eventTimes(i18n: I18n, event: AgentActivityEvent): string | null {
	return event.count && event.count > 1 ? i18n._(EV_TIMES, {count: event.count}) : null;
}

const SUB_RETRIED = msg({
	message: 'went through on the retry',
	comment: 'Agents activity: a failed prompt was sent again and worked.',
});
const SUB_SIGNED_IN_AGAIN = msg({
	message: 'signed in again {time}',
	comment: 'Agents activity: the account that signed out was signed in again at {time}.',
});
const SUB_BACK = msg({
	message: 'back {time}',
	comment: 'Agents activity: the machine or tool came back at {time}, after it was away.',
});
const SUB_SORTED = msg({
	message: 'sorted {time}',
	comment: 'Agents activity: a problem settled by itself at {time}.',
});
const SUB_SIGN_IN = msg({
	message: 'Sign in again from Health',
	comment: 'Agents activity: under an account that signed out and is still signed out.',
});
const SUB_QUOTED = msg({
	message: '“{title}”',
	comment: 'Agents activity: the chat a failed prompt was for, in quotes.',
});

/** An event's sub line: the chat or the machine's words, then how it settled. */
export function eventSub(i18n: I18n, event: AgentActivityEvent, now: number): string {
	const parts: Array<string> = [];
	if (event.threadTitle) parts.push(i18n._(SUB_QUOTED, {title: event.threadTitle}));
	if (event.detail && event.kind !== 'actionFailed') parts.push(event.detail);
	else if (event.detail && !event.threadTitle) parts.push(event.detail);
	const resolved = event.resolvedAt ? Date.parse(event.resolvedAt) : Number.NaN;
	if (Number.isFinite(resolved)) {
		const time = itemTime(i18n, resolved, now);
		if (event.kind === 'actionFailed') parts.push(i18n._(SUB_RETRIED));
		else if (event.kind === 'signedOut') parts.push(i18n._(SUB_SIGNED_IN_AGAIN, {time}));
		else if (event.kind === 'disconnected' || event.kind === 'toolDown') parts.push(i18n._(SUB_BACK, {time}));
		else parts.push(i18n._(SUB_SORTED, {time}));
	} else if (event.kind === 'signedOut') parts.push(i18n._(SUB_SIGN_IN));
	return parts.join(' · ');
}

const DID_SEND = msg({
	message: 'Kept your prompt in the chat, to send again or edit.',
	comment: 'Agents activity details, "What Konvo did" for a prompt that did not go through.',
});
const DID_SEND_RETRIED = msg({
	message: 'Kept your prompt and sent it again; that went through.',
	comment: 'Agents activity details, "What Konvo did" for a prompt that went through on the retry.',
});
const DID_START = msg({
	message: 'Kept the new chat on its page, to try again or edit. Nothing started on the machine.',
	comment: 'Agents activity details, "What Konvo did" for a chat that did not start.',
});
const DID_NOTHING_CHANGED = msg({
	message: 'Told you where it happened. Nothing else changed on the machine.',
	comment: 'Agents activity details, "What Konvo did" for another request that failed.',
});
const DID_SIGNED_OUT = msg({
	message: 'Marked the account signed out. Chats on it wait until you sign in again.',
	comment: 'Agents activity details, "What Konvo did" when an account signed out.',
});
const DID_BRIDGE_FAILED = msg({
	message: 'Kept the version it has running. konvo-agents tries the update again later.',
	comment: 'Agents activity details, "What Konvo did" when konvo-agents could not update itself.',
});
const DID_AGENT_FAILED = msg({
	message: 'Kept the version it has. You can try the update again from Agents.',
	comment: 'Agents activity details, "What Konvo did" when Claude Code or Codex could not update.',
});
const DID_DISCONNECTED = msg({
	message: 'Kept your chats as they were. They update again once the machine is back.',
	comment: 'Agents activity details, "What Konvo did" when the machine lost Konvo.',
});
const DID_TOOL_DOWN = msg({
	message: 'Waited for it to answer again. Nothing was lost.',
	comment: 'Agents activity details, "What Konvo did" when the agent tool stopped answering.',
});

/** What Konvo did about a problem event (its details), or null when there is nothing to say. */
export function eventKonvoDid(i18n: I18n, event: AgentActivityEvent): string | null {
	switch (event.kind) {
		case 'actionFailed':
			if (event.op === 'send') return event.resolvedAt ? i18n._(DID_SEND_RETRIED) : i18n._(DID_SEND);
			if (event.op === 'start') return i18n._(DID_START);
			return i18n._(DID_NOTHING_CHANGED);
		case 'signedOut':
			return i18n._(DID_SIGNED_OUT);
		case 'bridgeUpdateFailed':
			return i18n._(DID_BRIDGE_FAILED);
		case 'agentUpdateFailed':
			return i18n._(DID_AGENT_FAILED);
		case 'disconnected':
			return i18n._(DID_DISCONNECTED);
		case 'toolDown':
			return i18n._(DID_TOOL_DOWN);
		default:
			return null;
	}
}

const TODAY = msg({message: 'Today', comment: 'Agents activity: heading over today’s events.'});
const YESTERDAY = msg({message: 'Yesterday', comment: 'Agents activity: heading over yesterday’s events.'});

/** A day heading: Today, Yesterday, else "Monday 26 October". */
export function dayHeading(i18n: I18n, start: number, now: number): string {
	const today = new Date(now).setHours(0, 0, 0, 0);
	if (start >= today) return i18n._(TODAY);
	if (start >= new Date(today - 12 * 3600_000).setHours(0, 0, 0, 0)) return i18n._(YESTERDAY);
	return new Intl.DateTimeFormat(i18n.locale, {weekday: 'long', day: 'numeric', month: 'long'}).format(start);
}

const VERSIONS = msg({
	message: 'konvo-agents {bridge} · {tool} {toolVersion}',
	comment: 'Agents activity details: the versions when it happened, e.g. "konvo-agents 0.50.0 · T3 Code 0.1.3".',
});

/** "konvo-agents 0.50.0 · T3 Code 0.1.3" (or less, with what is known). */
export function versionsText(
	i18n: I18n,
	bridge: string | undefined,
	toolName: string,
	toolVersion: string | undefined,
): string {
	if (bridge && toolVersion) return i18n._(VERSIONS, {bridge, tool: toolName, toolVersion});
	if (bridge) return `konvo-agents ${bridge}`;
	if (toolVersion) return `${toolName} ${toolVersion}`;
	return '';
}

const REPORT_TITLE = msg({
	message: 'Konvo Agents: what happened on a machine',
	comment: 'Agents "Copy for support": first line of the copied text.',
});
const REPORT_MACHINE = msg({message: 'Machine: {machine}', comment: 'Agents "Copy for support": the machine line.'});
const REPORT_VERSIONS = msg({
	message: 'Versions: {versions}',
	comment: 'Agents "Copy for support": konvo-agents and agent tool versions.',
});
const REPORT_TIME = msg({message: 'Time: {time}', comment: 'Agents "Copy for support": when it happened (ISO time).'});
const REPORT_WHAT = msg({
	message: 'What: {title} ({kind})',
	comment: 'Agents "Copy for support": the event in words and its kind code.',
});
const REPORT_OP = msg({message: 'Action: {op}', comment: 'Agents "Copy for support": which request failed (code).'});
const REPORT_CODE = msg({message: 'Code: {code}', comment: 'Agents "Copy for support": the error code.'});
const REPORT_REF = msg({message: 'Ref: {ref}', comment: 'Agents "Copy for support": Konvo’s reference for the event.'});
const REPORT_DETAIL = msg({
	message: 'Detail: {detail}',
	comment: 'Agents "Copy for support": the machine’s own words.',
});
const REPORT_COUNT = msg({
	message: 'Times: {count}',
	comment: 'Agents "Copy for support": how often it happened within minutes.',
});

export interface ReportMachine {
	name: string;
	platform?: string;
	bridgeVersion?: string;
	toolName: string;
	toolVersion?: string;
}

/** The plain-text block "Copy for support" copies: machine, versions, time, kind, code, ref, detail. */
export function supportReport(i18n: I18n, machine: ReportMachine, event: AgentActivityEvent): string {
	const versions = versionsText(
		i18n,
		event.bridgeVersion ?? machine.bridgeVersion,
		machine.toolName,
		event.toolVersion ?? machine.toolVersion,
	);
	const machineLine = machine.platform ? `${machine.name} (${machine.platform})` : machine.name;
	return [
		i18n._(REPORT_TITLE),
		i18n._(REPORT_MACHINE, {machine: machineLine}),
		...(versions ? [i18n._(REPORT_VERSIONS, {versions})] : []),
		i18n._(REPORT_TIME, {time: event.at}),
		i18n._(REPORT_WHAT, {title: eventTitle(i18n, event, machine.toolName), kind: event.kind}),
		...(event.op ? [i18n._(REPORT_OP, {op: event.op})] : []),
		...(event.code ? [i18n._(REPORT_CODE, {code: event.code})] : []),
		...(event.id ? [i18n._(REPORT_REF, {ref: event.id})] : []),
		...(event.count && event.count > 1 ? [i18n._(REPORT_COUNT, {count: event.count})] : []),
		...(event.detail ? [i18n._(REPORT_DETAIL, {detail: event.detail})] : []),
	].join('\n');
}
